/**
 * Mattoni condivisi della maschera Monitoraggio: tessera numerica, badge di
 * stato, chip di segnalazione, badge di qualita' del dato. Ogni colore e'
 * sempre accompagnato da un'icona o da un testo: nessuno stato si legge dal
 * solo colore. Spec: docs/monitoring/ANALYSIS.md.
 */
import {
	AlertTriangle,
	CheckCircle2,
	Circle,
	CircleDashed,
	CircleDot,
	Info,
	Minus,
} from "lucide-react";
import { dayStatusMeta, flagLabel, qualityLabelKey } from "../../lib/monitoringUtils";
import { useMediaQuery } from "../../lib/useMediaQuery";

/**
 * Sotto i 768px la maschera cambia forma: schede al posto delle tabelle, giorni
 * a righe da sette al posto del registro largo. Una tabella da sette colonne sul
 * telefono si legge solo scorrendo di lato, e i tooltip col dito non si aprono.
 * Si decide in JS e non con le classi perche' le due forme sono strutture
 * diverse: renderle entrambe raddoppierebbe il registro (atleti × giorni).
 */
export function useIsCompact() {
	return !useMediaQuery("(min-width: 768px)");
}

const TONE_CLASS = {
	volt: "border-volt/30 bg-volt/10 text-volt",
	amber: "border-amber-400/40 bg-amber-400/10 text-amber-600 dark:text-amber-400",
	blaze: "border-blaze/30 bg-blaze/10 text-blaze",
	sky: "border-sky-400/40 bg-sky-400/10 text-sky-600 dark:text-sky-400",
	muted: "border-border bg-muted/40 text-muted-foreground",
};

// Il valore di una tessera: colore del testo soltanto, leggibile nei due temi.
const VALUE_TEXT_CLASS = {
	volt: "text-volt",
	amber: "text-amber-600 dark:text-amber-400",
	blaze: "text-blaze",
	sky: "text-sky-600 dark:text-sky-400",
	muted: "text-foreground",
};

export const STATUS_ICON = {
	complete: CheckCircle2,
	partial: CircleDot,
	none: Circle,
	upcoming: CircleDashed,
	rest: Minus,
};

export function toneClass(tone) {
	return TONE_CLASS[tone] || TONE_CLASS.muted;
}

/** Tessera numerica: etichetta, valore, nota. Il valore e' in proporzionale, mai tabulare. */
export function StatTile({ label, value, hint, tone = "muted" }) {
	return (
		<div className="rounded-sm border border-border bg-card px-3 py-2 md:px-4 md:py-3 min-w-0">
			{/* Sul telefono l'etichetta e la nota vanno a capo: troncate perdevano proprio i numeri. */}
			<div className="text-[10px] uppercase tracking-widest text-muted-foreground leading-tight md:truncate">
				{label}
			</div>
			<div
				className={`font-heading text-xl md:text-2xl font-bold leading-tight mt-1 ${
					VALUE_TEXT_CLASS[tone] || VALUE_TEXT_CLASS.muted
				}`}
			>
				{value}
			</div>
			{hint ? (
				<div className="text-[11px] text-muted-foreground mt-0.5 leading-snug md:truncate">{hint}</div>
			) : null}
		</div>
	);
}

export function StatusBadge({ status, t, className = "" }) {
	const meta = dayStatusMeta(status);
	const Icon = STATUS_ICON[status] || Minus;
	return (
		<span
			className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${toneClass(
				meta.tone,
			)} ${className}`}
		>
			<Icon className="w-3 h-3" aria-hidden="true" />
			{t(meta.labelKey)}
		</span>
	);
}

export function FlagChip({ flag, t, language }) {
	const warn = flag?.severity === "warn";
	const Icon = warn ? AlertTriangle : Info;
	return (
		<span
			className={`inline-flex items-start gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] leading-snug ${toneClass(
				warn ? "amber" : "muted",
			)}`}
		>
			<Icon className="w-3 h-3 mt-[1px] shrink-0" aria-hidden="true" />
			<span>{flagLabel(flag, t, language)}</span>
		</span>
	);
}

const QUALITY_TONE = {
	recorded: "volt",
	from_prescription: "sky",
	from_confirmation: "sky",
	from_score: "sky",
	partial: "amber",
	indeterminate: "muted",
};

export function QualityBadge({ quality, t }) {
	return (
		<span
			className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${toneClass(
				QUALITY_TONE[quality] || "muted",
			)}`}
		>
			{t(qualityLabelKey(quality))}
		</span>
	);
}
